import { useState } from 'react'
import { Link } from 'react-router'
import { daysUntil } from '@shared/dates'
import { VivaWordmark } from '@web/components/brand/Logo'
import { useAuth } from '@web/lib/auth'
import { useStudents } from '@web/lib/admin'
import { MistakesView } from './MistakesView'
import { StudentView } from './StudentView'
import { VocabularyEditor } from './VocabularyEditor'

type Tab = 'progress' | 'mistakes' | 'vocabulary'

/**
 * Teacher dashboard.
 *
 * Built for a native speaker looking at a learner's data, not for a course
 * administrator: the default view answers "how is she doing" and "what is she
 * getting wrong", because those are the questions that lead to a fix. Bulk
 * authoring stays in the seed files, where diffs and review work properly.
 */
export function AdminPage() {
  const { user } = useAuth()
  const { data, isPending } = useStudents()
  const [tab, setTab] = useState<Tab>('progress')
  const [studentId, setStudentId] = useState<string | null>(null)

  const students = data?.students ?? []
  const selected = studentId ?? students[0]?.id ?? null
  const student = students.find((s) => s.id === selected)

  if (user && user.role === 'student') {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <p className="text-ink-700">This area is for teachers.</p>
        <Link to="/" className="mt-4 inline-block font-semibold text-brand-600">
          Back to lessons
        </Link>
      </div>
    )
  }

  return (
    <div className="min-h-dvh bg-sand-50">
      <header className="border-b border-sand-200 bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <VivaWordmark className="text-xl" />
            <span className="rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-semibold tracking-wide text-brand-700 uppercase">
              Teacher
            </span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            {students.length > 1 && (
              <select
                value={selected ?? ''}
                onChange={(e) => setStudentId(e.target.value)}
                aria-label="Student"
                className="min-h-9 rounded-lg bg-white px-2 ring-1 ring-sand-200"
              >
                {students.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.displayName}
                  </option>
                ))}
              </select>
            )}
            <Link to="/" className="font-medium text-brand-600">
              Exit
            </Link>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 py-6">
        {isPending && <p className="text-ink-500">Loading…</p>}

        {!isPending && students.length === 0 && (
          <p className="rounded-card bg-white p-6 text-ink-700 ring-1 ring-brand-900/5">
            No student accounts yet.
          </p>
        )}

        {student && (
          <>
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="font-display text-3xl font-bold text-ink-900">
                  {student.displayName}
                </h1>
                <p className="mt-1 text-sm text-ink-500">
                  {student.email}
                  {student.travelDate && (
                    <> · {daysUntil(student.travelDate, 'America/New_York')} days to Brazil</>
                  )}
                </p>
              </div>
              <dl className="flex gap-6">
                <Stat label="Streak" value={`${student.currentStreak}`} suffix="days" />
                <Stat label="Lessons" value={`${student.lessonsCompleted}`} suffix="done" />
                <Stat label="XP" value={`${student.totalXp}`} />
              </dl>
            </div>

            <nav className="mb-5 flex gap-1 border-b border-sand-200">
              {(
                [
                  ['progress', 'Progress'],
                  ['mistakes', 'Common mistakes'],
                  ['vocabulary', 'Vocabulary'],
                ] as Array<[Tab, string]>
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  aria-current={tab === key ? 'page' : undefined}
                  className={`-mb-px min-h-10 border-b-2 px-4 text-sm font-semibold transition-colors ${
                    tab === key
                      ? 'border-brand-600 text-brand-700'
                      : 'border-transparent text-ink-500 hover:text-ink-900'
                  }`}
                >
                  {label}
                </button>
              ))}
            </nav>

            {tab === 'progress' && <StudentView userId={student.id} />}
            {tab === 'mistakes' && <MistakesView userId={student.id} />}
            {tab === 'vocabulary' && <VocabularyEditor />}
          </>
        )}
      </div>
    </div>
  )
}

function Stat({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div>
      <dt className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</dt>
      <dd className="font-display text-2xl font-bold text-ink-900">
        {value}
        {suffix && <span className="ml-1 text-sm font-normal text-ink-500">{suffix}</span>}
      </dd>
    </div>
  )
}
